import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, ShoppingBag, ArrowUpRight, Activity, Scale, Zap, Sparkles, Hourglass, Moon, Star } from 'lucide-react';
import { Vial } from './Vial';
import type { Product } from './products';
import { getProductReview } from './products';
import { useCart } from './cart/CartContext';
type Props = {
  product: Product;
  detailed?: boolean;
};
const GOAL_META: Record<string, {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  short: string;
}> = {
  Recovery: {
    icon: Activity,
    short: 'Recovery'
  },
  'Weight management': {
    icon: Scale,
    short: 'Weight loss'
  },
  Performance: {
    icon: Zap,
    short: 'Performance'
  },
  'Skin & Hair': {
    icon: Sparkles,
    short: 'Skin & hair'
  },
  Longevity: {
    icon: Hourglass,
    short: 'Longevity'
  },
  Sleep: {
    icon: Moon,
    short: 'Sleep'
  }
};
export function ProductCard({
  product,
  detailed = false
}: Props) {
  const {
    addItem
  } = useCart();
  const review = getProductReview(product);
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleAdd = (e: React.MouseEvent) => {
    stop(e);
    addItem(product.id, 1);
  };
  // Scaled purity fill — maps 97%–100% across the bar
  const purityFill = Math.max(2, Math.min(100, (product.purity - 97) / 3 * 100));
  return <motion.article whileHover={{
    y: -3
  }} transition={{
    duration: 0.25,
    ease: 'easeOut'
  }} className="group relative flex flex-col rounded-2xl border border-border bg-card overflow-hidden hover:border-foreground/20 transition-colors" data-id="element-533">
      <Link to={`/shop/${product.slug}`} aria-label={`View ${product.name}`} className="flex flex-col flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background" data-id="element-534">
        {/* Visual */}
        <div className="relative aspect-[5/4] bg-secondary/50 overflow-hidden border-b border-border flex items-center justify-center" data-id="element-535">
          {/* Vial — centered, breathing room */}
          <motion.div className="text-foreground" initial={false} whileHover={{
          y: -3
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} data-id="element-536">
            <Vial label={product.name} doseMg={product.doseMg} volumeMl={product.volumeMl} category={product.category} size={180} data-id="element-537" />
          </motion.div>

          {/* Badge — top-left */}
          {product.badge && <span className="absolute top-3.5 left-3.5 inline-flex items-center gap-1.5 rounded-full font-medium text-[10px] uppercase tracking-[0.06em] px-2 py-1 backdrop-blur-sm border bg-background/95 border-border text-foreground" data-id="element-538">
              {(product.badge === 'Bestseller' || product.badge === 'New') && <span className="h-1 w-1 rounded-full bg-primary" data-id="element-539" />}
              {product.badge}
            </span>}

          {/* Rating — top-right, quiet */}
          <div className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background/95 backdrop-blur-sm border border-border text-foreground text-[10px] font-medium leading-none" data-id="element-540">
            <Star className="h-2.5 w-2.5 fill-primary text-primary" strokeWidth={0} data-id="element-541" />
            <span className="font-mono [font-feature-settings:'tnum']" data-id="element-542">
              {review.rating}
            </span>
            <span className="text-muted-foreground font-mono [font-feature-settings:'tnum']" data-id="element-543">
              ({review.reviewCount})
            </span>
          </div>

          {/* Hover arrow */}
          <div className="absolute bottom-3.5 right-3.5 h-8 w-8 rounded-full bg-foreground text-background flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300" data-id="element-544">
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} data-id="element-545" />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-5" data-id="element-546">
          {/* Goal chips — replaces the old category eyebrow with richer tag chips */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3 min-h-[20px]" data-id="element-547">
            {product.goals.slice(0, 2).map(g => {
            const meta = GOAL_META[g];
            if (!meta) return null;
            const Icon = meta.icon;
            return <span key={g} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/70 text-foreground text-[10.5px] font-medium leading-none" data-id="element-548">
                  <Icon className="h-2.5 w-2.5 text-primary" strokeWidth={2} data-id="element-549" />
                  {meta.short}
                </span>;
          })}
            {product.goals.length > 2 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-secondary/70 text-muted-foreground text-[10.5px] font-medium leading-none" data-id="element-550">
                +{product.goals.length - 2}
              </span>}
            <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-551">
              {product.category}
            </span>
          </div>

          {/* Name + Price */}
          <div className="flex items-start justify-between gap-3" data-id="element-552">
            <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-foreground leading-tight" data-id="element-553">
              {product.name}
            </h3>
            <span className="font-mono text-[16px] font-medium text-foreground whitespace-nowrap leading-tight [font-feature-settings:'tnum']" data-id="element-554">
              £{product.price}
            </span>
          </div>

          {/* Tagline */}
          <p className="mt-2 text-[13px] text-muted-foreground line-clamp-2 leading-relaxed min-h-[2.4rem]" data-id="element-555">
            {product.tagline}
          </p>

          {/* Spec row */}
          <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-muted-foreground [font-feature-settings:'tnum']" data-id="element-556">
            <span data-id="element-557">
              <span className="text-foreground" data-id="element-558">{product.doseMg}</span>
              <span className="text-primary font-semibold" data-id="element-559">mg</span>
              <span className="text-border mx-2" data-id="element-560">·</span>
              <span data-id="element-561">lyophilised</span>
            </span>
            <span className="text-border" data-id="element-562">{product.batch}</span>
          </div>

          {/* Purity gauge — simple: label + value + bar */}
          <div className="mt-3" data-id="element-563">
            <div className="flex items-baseline justify-between mb-1.5 text-[10.5px] font-mono [font-feature-settings:'tnum']" data-id="element-564">
              <span className="uppercase tracking-wider text-muted-foreground" data-id="element-565">
                HPLC purity
              </span>
              <span className="text-foreground font-semibold" data-id="element-566">
                {product.purity.toFixed(1)}%
              </span>
            </div>
            <div className="relative h-[4px] w-full rounded-full bg-secondary overflow-hidden" role="progressbar" aria-valuemin={97} aria-valuemax={100} aria-valuenow={product.purity} aria-label={`HPLC purity ${product.purity.toFixed(1)}%`} data-id="element-567">
              <div className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all" style={{
              width: `${purityFill}%`
            }} data-id="element-568" />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex items-center gap-2 pt-4 border-t border-border" data-id="element-569">
            <button onClick={handleAdd} disabled={!product.inStock} className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground text-[13px] font-medium tracking-[-0.01em] leading-none transition-[transform,box-shadow] duration-200 ease-out [box-shadow:0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_rgba(20,60,30,0.15),0_3px_8px_-3px_rgba(20,60,30,0.30)] hover:-translate-y-px hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.22)_inset,0_2px_4px_rgba(20,60,30,0.18),0_8px_18px_-5px_rgba(20,60,30,0.42)] active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none" data-id="element-570">
              <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} data-id="element-571" />
              Add to cart
            </button>
            <button onClick={stop} aria-label="View certificate of analysis" className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" data-id="element-572">
              <FileText className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-573" />
              <span className="text-[10px] font-mono uppercase tracking-wider" data-id="element-574">
                COA
              </span>
            </button>
          </div>
        </div>
      </Link>
    </motion.article>;
}